import React, { useState } from 'react';
import { FaStar, FaRegStar, FaTrash, FaPlus, FaUsers, FaSearch, FaTimes, FaLink } from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';

/**
 * Seleção de múltiplos responsáveis com marcação do principal.
 * Props:
 *  - potenciais: array de adultos { id, nomeCompleto | nome_completo | nome }
 *  - value: array de { id, principal }
 *  - onChange: (novoArray) => void
 *  - disabled: boolean
 *  - criancaId: number | string (opcional) — quando fornecido, persiste o vínculo
 *    imediatamente após criar um novo responsável (modo edição)
 *  - onMembroAdicionado: (criado) => void — callback para atualizar lista de potenciais
 */
const nomeDe = (r) => r?.nomeCompleto || r?.nome_completo || r?.nome || `ID ${r?.id}`;

const SeletorResponsaveis = ({
  potenciais = [],
  value = [],
  onChange,
  disabled = false,
  criancaId,
  onMembroAdicionado,
}) => {
  const [aAdicionar, setAAdicionar] = useState('');
  const [busca, setBusca] = useState('');
  const [modalNovoAberto, setModalNovoAberto] = useState(false);
  const [salvandoNovo, setSalvandoNovo] = useState(false);
  const [novoMembro, setNovoMembro] = useState({
    nomeCompleto: '',
    tipoPessoa: 'irma',
    telefone: '',
    estadoCivil: 'casado',
  });

  const mapaNomes = React.useMemo(() => {
    const m = new Map();
    potenciais.forEach((p) => m.set(Number(p.id), nomeDe(p)));
    return m;
  }, [potenciais]);

  const idsSelecionados = new Set(value.map((v) => Number(v.id)));
  const disponiveis = potenciais.filter((p) => !idsSelecionados.has(Number(p.id)));
  const disponiveisFiltrados = busca.trim()
    ? disponiveis.filter((p) => nomeDe(p).toLowerCase().includes(busca.toLowerCase()))
    : disponiveis;

  const adicionar = () => {
    const id = parseInt(aAdicionar, 10);
    if (Number.isNaN(id) || idsSelecionados.has(id)) return;

    // Verifica duplicidade explícita
    if (idsSelecionados.has(id)) {
      toast.info('Este responsável já está vinculado a esta criança.');
      return;
    }

    const novo = [...value, { id, principal: value.length === 0 }];
    onChange(novo);
    setAAdicionar('');
    setBusca('');
  };

  const remover = (id) => {
    let novo = value.filter((v) => Number(v.id) !== Number(id));
    if (novo.length > 0 && !novo.some((v) => v.principal)) {
      novo = novo.map((v, i) => ({ ...v, principal: i === 0 }));
    }
    onChange(novo);
  };

  const definirPrincipal = (id) => {
    onChange(value.map((v) => ({ ...v, principal: Number(v.id) === Number(id) })));
  };

  const salvarNovoMembro = async () => {
    const nomeTrim = novoMembro.nomeCompleto.trim();
    if (!nomeTrim) {
      toast.warn('Informe o nome completo');
      return;
    }

    const nomeNorm = nomeTrim.toLowerCase();
    const jaExiste = potenciais.find(
      (p) => nomeDe(p).trim().toLowerCase() === nomeNorm
    );
    if (jaExiste) {
      toast.info('Este nome já está na lista. Selecione-o para vincular.');
      setBusca(nomeDe(jaExiste));
      setAAdicionar(String(jaExiste.id));
      setModalNovoAberto(false);
      return;
    }

    try {
      setSalvandoNovo(true);

      // 1. Cria o responsável (estado civil casado/divorciado/viuvo → adulto na API)
      const resp = await api.post('/irmandade', {
        nome_completo: nomeTrim,
        tipo_pessoa: novoMembro.tipoPessoa,
        telefone: novoMembro.telefone.trim() || null,
        data_nascimento: '1990-01-01',
        estado_civil: novoMembro.estadoCivil,
      });
      const raw = resp.data?.data ?? resp.data;
      // API antiga devolvia só o id (number); normaliza para objeto
      const criado = typeof raw === 'number' || typeof raw === 'string'
        ? { id: Number(raw), nomeCompleto: nomeTrim, nome_completo: nomeTrim }
        : {
            ...raw,
            id: Number(raw.id),
            nomeCompleto: raw.nomeCompleto || raw.nome_completo || nomeTrim,
            nome_completo: raw.nome_completo || raw.nomeCompleto || nomeTrim,
          };

      if (!criado?.id) {
        toast.error('Responsável cadastrado, mas a resposta da API veio incompleta. Recarregue a página.');
        return;
      }

      // 2. Se há criancaId (modo edição), persiste o vínculo imediatamente no banco
      if (criancaId) {
        try {
          await api.post(`/irmandade/${criancaId}/vincular-responsavel`, {
            responsavel_id: criado.id,
            principal: value.length === 0,
          });
          toast.success('Responsável cadastrado e vinculado à criança com sucesso.');
        } catch (errVinculo) {
          const msgVinculo = errVinculo.response?.data?.error || '';
          if (msgVinculo.includes('já está vinculado')) {
            toast.info('Este responsável já estava vinculado a esta criança.');
          } else {
            toast.warn(
              'Responsável cadastrado, mas não foi possível vincular automaticamente. Selecione-o manualmente.'
            );
          }
        }
      } else {
        toast.success(`${nomeTrim} cadastrado e selecionado.`);
      }

      setModalNovoAberto(false);
      setNovoMembro({ nomeCompleto: '', tipoPessoa: 'irma', telefone: '', estadoCivil: 'casado' });
      setBusca('');
      setAAdicionar('');

      // 3. Atualiza lista de potenciais + seleção no formulário pai
      if (onMembroAdicionado) {
        onMembroAdicionado(criado);
      } else if (!idsSelecionados.has(criado.id)) {
        // Fallback: vincula localmente mesmo sem callback do pai
        onChange([...value, { id: criado.id, principal: value.length === 0 }]);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error ||
        'Não foi possível cadastrar o responsável. Verifique os dados e tente novamente.'
      );
    } finally {
      setSalvandoNovo(false);
    }
  };

  const modoEdicao = !!criancaId;

  return (
    <div className="resp-multi">
      {value.length === 0 && (
        <p className="resp-multi-vazio">
          <FaUsers /> Nenhum responsável vinculado ainda.
        </p>
      )}

      {value.length > 0 && (
        <ul className="resp-multi-lista">
          {value.map((v) => (
            <li key={v.id} className={`resp-multi-item${v.principal ? ' principal' : ''}`}>
              <button
                type="button"
                className="resp-multi-star"
                title={v.principal ? 'Responsável principal' : 'Definir como principal'}
                onClick={() => definirPrincipal(v.id)}
                disabled={disabled}
              >
                {v.principal ? <FaStar /> : <FaRegStar />}
              </button>
              <span className="resp-multi-nome">{mapaNomes.get(Number(v.id)) || `ID ${v.id}`}</span>
              {v.principal && <span className="resp-multi-badge">Principal</span>}
              <button
                type="button"
                className="resp-multi-remover"
                title="Remover responsável"
                onClick={() => remover(v.id)}
                disabled={disabled}
              >
                <FaTrash />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="resp-multi-add">
        {/* Busca para filtrar o select */}
        <div className="resp-multi-busca-wrap">
          <FaSearch className="resp-multi-busca-icone" />
          <input
            type="text"
            className="resp-multi-busca"
            placeholder="Filtrar responsável por nome…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            disabled={disabled || disponiveis.length === 0}
          />
          {busca && (
            <button
              type="button"
              className="resp-multi-busca-limpar"
              onClick={() => setBusca('')}
              title="Limpar busca"
            >
              <FaTimes size={11} />
            </button>
          )}
        </div>

        <div className="resp-multi-add-row">
          <select
            value={aAdicionar}
            onChange={(e) => setAAdicionar(e.target.value)}
            disabled={disabled || disponiveisFiltrados.length === 0}
          >
            <option value="">
              {disponiveisFiltrados.length === 0
                ? busca
                  ? 'Nenhum resultado para a busca'
                  : 'Nenhum outro adulto disponível'
                : 'Selecione para vincular'}
            </option>
            {disponiveisFiltrados.map((p) => (
              <option key={p.id} value={p.id}>
                {nomeDe(p)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="resp-multi-add-btn"
            onClick={adicionar}
            disabled={disabled || !aAdicionar}
            title="Vincular responsável selecionado"
          >
            <FaLink /> Vincular
          </button>
        </div>

        {/* Botão para criar e vincular novo responsável */}
        {!disabled && (
          <button
            type="button"
            className="resp-multi-novo-btn"
            onClick={() => setModalNovoAberto(true)}
          >
            <FaPlus />
            {modoEdicao ? ' Criar e vincular novo responsável' : ' Cadastrar novo responsável'}
          </button>
        )}
      </div>

      <span className="campo-dica">
        Clique na estrela para definir quem é o responsável principal. O principal é usado no
        mapa, nas visitas e nos relatórios.
      </span>

      {/* Modal de cadastro de novo responsável */}
      {modalNovoAberto && (
        <div className="resp-modal-overlay" onClick={() => setModalNovoAberto(false)}>
          <div className="resp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="resp-modal-header">
              <h3>
                <FaUsers />{' '}
                {modoEdicao ? 'Criar e vincular novo responsável' : 'Cadastrar responsável'}
              </h3>
              <button
                type="button"
                onClick={() => setModalNovoAberto(false)}
                className="resp-modal-fechar"
                disabled={salvandoNovo}
              >
                <FaTimes />
              </button>
            </div>

            <div className="resp-modal-body">
              <div className="resp-modal-grupo">
                <label>Nome completo *</label>
                <input
                  type="text"
                  value={novoMembro.nomeCompleto}
                  onChange={(e) =>
                    setNovoMembro((prev) => ({ ...prev, nomeCompleto: e.target.value }))
                  }
                  placeholder="Nome completo do responsável"
                  autoFocus
                  disabled={salvandoNovo}
                />
              </div>

              <div className="resp-modal-grupo">
                <label>Tipo</label>
                <select
                  value={novoMembro.tipoPessoa}
                  onChange={(e) =>
                    setNovoMembro((prev) => ({ ...prev, tipoPessoa: e.target.value }))
                  }
                  disabled={salvandoNovo}
                >
                  <option value="irma">Irmã</option>
                  <option value="irmao">Irmão</option>
                </select>
              </div>

              <div className="resp-modal-grupo">
                <label>Estado civil *</label>
                <select
                  value={novoMembro.estadoCivil}
                  onChange={(e) =>
                    setNovoMembro((prev) => ({ ...prev, estadoCivil: e.target.value }))
                  }
                  disabled={salvandoNovo}
                >
                  {/* Só opções que geram categoria "adulto" — jovens não entram na lista de responsáveis */}
                  <option value="casado">Casado(a)</option>
                  <option value="divorciado">Divorciado(a)</option>
                  <option value="viuvo">Viúvo(a)</option>
                </select>
              </div>

              <div className="resp-modal-grupo">
                <label>Telefone (opcional)</label>
                <input
                  type="tel"
                  value={novoMembro.telefone}
                  onChange={(e) =>
                    setNovoMembro((prev) => ({ ...prev, telefone: e.target.value }))
                  }
                  placeholder="(14) 99999-9999"
                  disabled={salvandoNovo}
                />
              </div>

              {modoEdicao ? (
                <p className="resp-modal-dica resp-modal-dica-destaque">
                  <FaLink style={{ marginRight: 4 }} />
                  O responsável será cadastrado e <strong>vinculado automaticamente</strong> à
                  criança. Não é necessário selecionar depois.
                </p>
              ) : (
                <p className="resp-modal-dica">
                  Será cadastrado como adulto. Para adicionar mais informações, edite depois na
                  página de Irmandade.
                </p>
              )}
            </div>

            <div className="resp-modal-footer">
              <button
                type="button"
                className="resp-modal-btn-cancelar"
                onClick={() => setModalNovoAberto(false)}
                disabled={salvandoNovo}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="resp-modal-btn-salvar"
                onClick={salvarNovoMembro}
                disabled={salvandoNovo || !novoMembro.nomeCompleto.trim()}
              >
                {salvandoNovo ? (
                  modoEdicao ? 'Cadastrando e vinculando…' : 'Cadastrando…'
                ) : (
                  <>
                    <FaLink style={{ marginRight: 4 }} />
                    {modoEdicao ? 'Cadastrar e vincular' : 'Cadastrar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeletorResponsaveis;
